import { useMemo, useState } from 'react';
import { BedDouble, Users } from 'lucide-react';

interface Bed {
  id: string;
  bedNumber: string;
  ward: string;
  status: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'MAINTENANCE';
  type?: string;
  price?: number;
  patientName?: string;
}

interface Ward {
  id: string;
  wardCode: string;
  wardName: string;
  wardType: string;
  floor?: string;
  totalBeds: number;
}

interface BedFloorPlanProps {
  beds: Bed[];
  wards: Ward[];
  onBedClick?: (bed: Bed) => void;
  filterWard?: string;
}

const STATUS_META: Record<string, {
  label: string;
  color: string;
  bg: string;
  border: string;
  dot: string;
  barColor: string;
  lightBg: string;
}> = {
  AVAILABLE: {
    label: 'Available',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
    barColor: 'bg-emerald-500',
    lightBg: 'bg-emerald-100',
  },
  OCCUPIED: {
    label: 'Occupied',
    color: 'text-rose-700',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    dot: 'bg-rose-500',
    barColor: 'bg-rose-500',
    lightBg: 'bg-rose-100',
  },
  RESERVED: {
    label: 'Reserved',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
    barColor: 'bg-amber-500',
    lightBg: 'bg-amber-100',
  },
  MAINTENANCE: {
    label: 'Maintenance',
    color: 'text-slate-600',
    bg: 'bg-slate-100',
    border: 'border-slate-200',
    dot: 'bg-slate-400',
    barColor: 'bg-slate-400',
    lightBg: 'bg-slate-200',
  },
};

const WARD_BG_COLORS = [
  'bg-emerald-50/40',
  'bg-rose-50/40',
  'bg-amber-50/40',
  'bg-slate-50/40',
  'bg-violet-50/40',
  'bg-cyan-50/40',
];

const WARD_BORDER_COLORS = [
  'border-emerald-200',
  'border-rose-200',
  'border-amber-200',
  'border-slate-200',
  'border-violet-200',
  'border-cyan-200',
];

export function BedFloorPlan({ beds, wards, onBedClick, filterWard }: BedFloorPlanProps) {
  const [hoveredBedId, setHoveredBedId] = useState<string | null>(null);

  const wardsWithBeds = useMemo(() => {
    const map = new Map<string, Bed[]>();
    for (const bed of beds) {
      const wn = bed.ward || 'Unassigned';
      if (!map.has(wn)) map.set(wn, []);
      map.get(wn)!.push(bed);
    }
    for (const [, list] of map) {
      list.sort((a, b) => (a.bedNumber || '').localeCompare(b.bedNumber || '', undefined, { numeric: true }));
    }
    return new Map([...map.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  }, [beds]);

  const filteredEntries = useMemo(() => {
    const entries = Array.from(wardsWithBeds.entries());
    if (!filterWard?.trim()) return entries;
    const q = filterWard.toLowerCase();
    return entries.filter(([name]) => name.toLowerCase().includes(q));
  }, [wardsWithBeds, filterWard]);

  if (filteredEntries.length === 0) {
    return (
      <div className="bg-surface rounded-2xl border border-border p-12 text-center shadow-sm">
        <div className="w-16 h-16 rounded-2xl bg-background flex items-center justify-center mx-auto mb-4">
          <BedDouble className="w-8 h-8 text-text-muted/40" />
        </div>
        <p className="text-lg font-semibold text-text">No wards found</p>
        <p className="text-sm text-text-muted mt-1">Add beds and wards to see the floor plan.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {filteredEntries.map(([wardName, wardBeds], wardIndex) => {
        const wardInfo = wards.find((w) => w.wardName === wardName);
        const occupiedCount = wardBeds.filter((b) => b.status === 'OCCUPIED').length;
        const availableCount = wardBeds.filter((b) => b.status === 'AVAILABLE').length;
        const reservedCount = wardBeds.filter((b) => b.status === 'RESERVED').length;
        const maintCount = wardBeds.filter((b) => b.status === 'MAINTENANCE').length;
        const rate = wardBeds.length > 0 ? Math.round((occupiedCount / wardBeds.length) * 100) : 0;
        const wardBg = WARD_BG_COLORS[wardIndex % WARD_BG_COLORS.length];
        const wardBorder = WARD_BORDER_COLORS[wardIndex % WARD_BORDER_COLORS.length];

        return (
          <div
            key={wardName}
            className={`rounded-2xl border-2 ${wardBorder} ${wardBg} shadow-sm overflow-hidden`}
          >
            {/* Ward Header */}
            <div className="px-5 py-4 border-b border-border/60 bg-white/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <BedDouble size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-text">{wardName}</h2>
                  <div className="flex items-center gap-2 text-xs text-text-muted mt-0.5">
                    <span className="uppercase tracking-wide">{wardInfo?.wardType?.replace('_', ' ') || 'General'}</span>
                    {wardInfo?.floor && (
                      <>
                        <span className="text-border">|</span>
                        <span>Floor {wardInfo.floor}</span>
                      </>
                    )}
                    <span className="text-border">|</span>
                    <span>{wardBeds.length} beds</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-5">
                {/* Mini progress bar */}
                <div className="hidden md:flex items-center gap-2">
                  <div className="w-28 h-2.5 rounded-full bg-background overflow-hidden flex shadow-inner">
                    <div className="h-full bg-emerald-500" style={{ width: `${(availableCount / wardBeds.length) * 100}%` }} />
                    <div className="h-full bg-rose-500" style={{ width: `${(occupiedCount / wardBeds.length) * 100}%` }} />
                    <div className="h-full bg-amber-400" style={{ width: `${(reservedCount / wardBeds.length) * 100}%` }} />
                    <div className="h-full bg-slate-300" style={{ width: `${(maintCount / wardBeds.length) * 100}%` }} />
                  </div>
                  <span className={`text-xs font-bold ${rate >= 90 ? 'text-rose-600' : rate >= 70 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {rate}%
                  </span>
                </div>

                <div className="flex items-center gap-3 text-xs">
                  {availableCount > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                      <span className="text-text-muted font-medium">{availableCount}</span>
                    </div>
                  )}
                  {occupiedCount > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                      <span className="text-text-muted font-medium">{occupiedCount}</span>
                    </div>
                  )}
                  {reservedCount > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                      <span className="text-text-muted font-medium">{reservedCount}</span>
                    </div>
                  )}
                  {maintCount > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-400" />
                      <span className="text-text-muted font-medium">{maintCount}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bed Grid — Floor Plan Style */}
            <div className="p-5">
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10 gap-3">
                {wardBeds.map((bed) => {
                  const meta = STATUS_META[bed.status] || STATUS_META.AVAILABLE;
                  const isHovered = hoveredBedId === bed.id;

                  return (
                    <button
                      key={bed.id}
                      onClick={() => onBedClick?.(bed)}
                      onMouseEnter={() => setHoveredBedId(bed.id)}
                      onMouseLeave={() => setHoveredBedId(null)}
                      className={`
                        group relative text-left rounded-xl border-2 p-3 flex flex-col gap-2 transition-all duration-200
                        ${meta.bg} ${meta.border}
                        ${isHovered ? 'shadow-lg -translate-y-1 ring-2 ring-primary/20' : 'shadow-sm hover:shadow-md hover:-translate-y-0.5'}
                      `}
                      title={`${bed.bedNumber} — ${meta.label}${bed.patientName ? ` (${bed.patientName})` : ''}${bed.type ? ` · ${bed.type}` : ''}`}
                    >
                      {/* Top bar */}
                      <div className={`h-1.5 rounded-full ${meta.barColor} mb-0.5`} />

                      {/* Icon + Status dot */}
                      <div className="flex items-center justify-between">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white/80 shadow-sm`}>
                          {bed.status === 'OCCUPIED' ? (
                            <Users size={15} className={meta.color} />
                          ) : (
                            <BedDouble size={15} className={meta.color} />
                          )}
                        </div>
                        <div className={`w-2 h-2 rounded-full ${meta.dot} ring-2 ring-white`} />
                      </div>

                      {/* Bed number */}
                      <div>
                        <p className={`text-sm font-bold ${meta.color} leading-tight`}>{bed.bedNumber}</p>
                        {bed.status === 'OCCUPIED' && bed.patientName ? (
                          <p className="text-[0.6875rem] text-text font-medium truncate mt-0.5 leading-tight" title={bed.patientName}>
                            {bed.patientName}
                          </p>
                        ) : (
                          <p className={`text-[0.6875rem] ${meta.color} opacity-70 mt-0.5 leading-tight`}>{meta.label}</p>
                        )}
                      </div>

                      {/* Type badge */}
                      {bed.type && (
                        <span className={`text-[0.5625rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-white/60 ${meta.color} w-fit`}>
                          {bed.type}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
